import { Injectable } from '@angular/core';

export interface EncryptedPayload {
  encryptedData: string;
  iv: string;
  serverPublicKey: string;
}

@Injectable({ providedIn: 'root' })
export class EcdhCryptoService {
  private cachedPrivateKey?: CryptoKey;
  private cachedPublicKeyBase64?: string;
  private keypairPromise?: Promise<{ publicKeyBase64: string; privateKey: CryptoKey }>;

  // ——— Session (ephemeral) keypair ———
  async getOrCreateClientKeyPair(): Promise<{ publicKeyBase64: string; privateKey: CryptoKey }> {
    if (this.cachedPrivateKey && this.cachedPublicKeyBase64) {
      return { publicKeyBase64: this.cachedPublicKeyBase64, privateKey: this.cachedPrivateKey };
    }

    this.keypairPromise ??= this.generateAndExportKeyPair()
      .then(({ publicKeyBase64, privateKey }) => {
        this.cachedPrivateKey = privateKey;
        this.cachedPublicKeyBase64 = publicKeyBase64;
        return { publicKeyBase64, privateKey };
      })
      .finally(() => {
        this.keypairPromise = undefined;
      });

    return this.keypairPromise;
  }

  /** Header name used to transmit the client public key */
  readonly clientPublicKeyHeaderName = 'X-Client-Public-Key';

  /** Convenience helper: returns header tuple and private key for decrypting server response */
  async getClientPublicKeyHeader(): Promise<{ headerName: string; headerValue: string; privateKey: CryptoKey }> {
    const { publicKeyBase64, privateKey } = await this.getOrCreateClientKeyPair();
    return { headerName: this.clientPublicKeyHeaderName, headerValue: publicKeyBase64, privateKey };
  }

  /** Explicitly rotate session keys (e.g., on logout/login) */
  async rotateSessionKeys(): Promise<void> {
    this.cachedPrivateKey = undefined;
    this.cachedPublicKeyBase64 = undefined;
    await this.getOrCreateClientKeyPair();
  }

  private async generateAndExportKeyPair(): Promise<{ publicKeyBase64: string; privateKey: CryptoKey }> {
    const keyPair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
    const spki = await crypto.subtle.exportKey('spki', keyPair.publicKey);
    const publicKeyBase64 = this.arrayBufferToBase64(spki);
    return { publicKeyBase64, privateKey: keyPair.privateKey };
  }

  // ——— ECDH → AES key derivation ———
  async deriveAesKey(privateKey: CryptoKey, serverPublicKeyBase64: string): Promise<CryptoKey> {
    const spki = this.base64ToArrayBuffer(this.normalizeBase64(serverPublicKeyBase64));
    const serverPublicKey = await crypto.subtle.importKey(
      'spki',
      spki,
      { name: 'ECDH', namedCurve: 'P-256' },
      false,
      []
    );
    const sharedBits = await crypto.subtle.deriveBits({ name: 'ECDH', public: serverPublicKey }, privateKey, 256);
    const hashed = await crypto.subtle.digest('SHA-256', sharedBits); // KDF: SHA-256
    return crypto.subtle.importKey('raw', hashed, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
  }

  // ——— Decrypt payload from backend ———
  isEncryptedPayload(obj: any): obj is EncryptedPayload {
    return (
      !!obj &&
      typeof obj?.encryptedData === 'string' &&
      typeof obj?.iv === 'string' &&
      typeof obj?.serverPublicKey === 'string'
    );
  }

  async decryptPayload<T = any>(payload: EncryptedPayload, privateKey: CryptoKey): Promise<T> {
    const aesKey = await this.deriveAesKey(privateKey, payload.serverPublicKey);
    const iv = new Uint8Array(this.base64ToArrayBuffer(this.normalizeBase64(payload.iv)));
    const ct = new Uint8Array(this.base64ToArrayBuffer(this.normalizeBase64(payload.encryptedData)));
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv, tagLength: 128 }, aesKey, ct);
    const json = new TextDecoder().decode(decrypted);
    return JSON.parse(json) as T;
  }

  // ——— b64 helpers ———
  private normalizeBase64(s: string): string {
    const t = (s ?? '').replaceAll(/\s+/g, '').replaceAll('-', '+').replaceAll('_', '/');
    const pad = (4 - (t.length % 4)) % 4;
    return t + '='.repeat(pad);
  }

  private base64ToArrayBuffer(b64: string): ArrayBuffer {
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (ch) => ch.charCodeAt(0) & 0xff);
    return bytes.buffer;
  }

  private arrayBufferToBase64(buf: ArrayBuffer): string {
    const bytes = new Uint8Array(buf);
    let bin = '';
    for (const b of bytes) bin += String.fromCodePoint(b);
    return btoa(bin);
  }
}
